import { PaginatedResponse } from "@/types";
import {
  AttendeeHistory,
  AttendeeHistoryCreateData,
} from "@/types/attendeeHistory";
import { parseDecimalNumber } from "@/utils/number";
import { normalizeWeightKgForPayload } from "@/utils/petWeight";
import { apiUrl } from "../../utils/url";
import { HttpClient, HttpError } from "../HttpClient";
import {
  AttendeeHistoryParams,
  AttendeeWeightHistoryParams,
  AttendeeWeightRecord,
  IAttendeeHistoryService,
} from "./IAttendeeHistoryService";

export class AttendeeHistoryService implements IAttendeeHistoryService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient({
      baseURL: apiUrl,
      timeout: 10000,
      headers: {},
    });
  }

  async getAttendeeHistory(
    params: AttendeeHistoryParams,
  ): Promise<PaginatedResponse<AttendeeHistory>> {
    const queryParams = new URLSearchParams();

    if (params.size !== undefined) {
      queryParams.append("size", params.size.toString());
    }

    if (params.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }

    if (params.sort) {
      queryParams.append("sort", params.sort);
    }

    if (params.serviceTypes && params.serviceTypes.length > 0) {
      params.serviceTypes.forEach(type => {
        queryParams.append("serviceTypes", type);
      });
    }

    if (params.servicesIds && params.servicesIds.length > 0) {
      params.servicesIds.forEach(id => {
        queryParams.append("servicesIds", id.toString());
      });
    }

    if (params.storeId !== undefined) {
      queryParams.append("storeId", params.storeId.toString());
    }

    if (params.appointmentStatus && params.appointmentStatus.length > 0) {
      params.appointmentStatus.forEach(status => {
        queryParams.append("appointmentStatus", status);
      });
    }

    if (params.startDate) {
      queryParams.append("startDate", params.startDate);
    }
    if (params.endDate) {
      queryParams.append("endDate", params.endDate);
    }

    if (params.onlyAppointmentHistory === true) {
      queryParams.append("onlyAppointmentHistory", "true");
    }

    const queryString = queryParams.toString();
    const endpoint = `/attendeeHistory/attendee/${params.attendeeId}${
      queryString ? `?${queryString}` : ""
    }`;

    try {
      const response = await this.httpClient.get<
        PaginatedResponse<AttendeeHistory>
      >(endpoint);

      return response;
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Não foi possível carregar o histórico do animal. Atualize a página e tente novamente.",
      );
    }
  }

  async getLatestAttendeeHistory(
    attendeeId: number,
  ): Promise<AttendeeHistory | null> {
    const response = await this.getAttendeeHistory({
      attendeeId,
      sort: "registeredOn,desc",
      page: 0,
      size: 1,
    });
    return response.content[0] ?? null;
  }

  async getAttendeeWeightHistory(
    params: AttendeeWeightHistoryParams,
  ): Promise<AttendeeWeightRecord[]> {
    const endpoint = `/attendeeHistory/attendee/${params.attendeeId}/weight-history`;

    try {
      const response = await this.httpClient.get<AttendeeWeightRecord[]>(
        endpoint,
      );

      return response;
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Não foi possível carregar o histórico de peso do animal. Atualize a página e tente novamente.",
      );
    }
  }

  async getAttendeeHistoryByAppointmentId(
    id: string,
  ): Promise<AttendeeHistory | null> {
    const endpoint = `/attendeeHistory/appointment/${id}`;
    try {
      const response = await this.httpClient.get<AttendeeHistory>(endpoint);
      return response;
    } catch (error) {
      if (error instanceof HttpError && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getAttendeeHistoryById(id: number): Promise<AttendeeHistory> {
    const endpoint = `/attendeeHistory/${id}`;
    try {
      const response = await this.httpClient.get<AttendeeHistory>(endpoint);
      return response;
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Não foi possível carregar o histórico do animal. Atualize a página e tente novamente.",
      );
    }
  }

  async createAttendeeHistory(
    data: AttendeeHistoryCreateData,
  ): Promise<number> {
    const endpoint = `/attendeeHistory`;

    try {
      const payload = {
        ...data,
        weightKg: normalizeWeightKgForPayload(data.weightKg),
        tempCelsius: parseDecimalNumber(data.tempCelsius),
      };
      const response = await this.httpClient.post<number>(
        endpoint,
        payload,
        {},
        true,
      );

      return response;
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Não foi possível criar o histórico do animal. Confirme os campos obrigatórios e tente novamente.",
      );
    }
  }

  async updateAttendeeHistory(
    id: number,
    data: Partial<AttendeeHistoryCreateData>,
  ): Promise<number> {
    try {
      const payload = {
        ...data,
        weightKg: normalizeWeightKgForPayload(data.weightKg),
        tempCelsius: parseDecimalNumber(data.tempCelsius),
      };
      const responseId = await this.httpClient.put<number>(
        `/attendeeHistory/${id}`,
        payload,
        {},
        true,
      );
      return responseId;
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Não foi possível atualizar o histórico do animal. Confirme os campos obrigatórios e tente novamente.",
      );
    }
  }
}
